import type { FC } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../Select/Select';
import FloatingLabelWrapper from './FloatingLabelWrapper';
import { FormControl, FormField, FormItem } from './Form';

type SelectOption = {
  value: string;
  label: string;
};

type FormSelectFieldProps = {
  name: string;
  form: UseFormReturn<any>;
  label: string;
  options: SelectOption[];
  defaultValue?: string;
  required?: boolean;
  disabled?: boolean;
  onChange?: (value: string) => void;
  dataTestId?: string;
};

export const GenericSelect: FC<FormSelectFieldProps> = ({
  name,
  form,
  label,
  options,
  defaultValue,
  required = false,
  disabled = false,
  onChange,
  dataTestId,
}) => (
  <FormField
    control={form.control}
    name={name}
    render={({ field }) => (
      <FormItem>
        <FormControl>
          <FloatingLabelWrapper
            fieldValue={field.value || defaultValue || ''}
            label={label}
            required={required}
            disabled={disabled}
          >
            {({ handleFocus, handleBlur, id }) => {
              const selectedValue = field.value || defaultValue || '';
              const selectedLabel = options.find(
                option => option.value === selectedValue,
              )?.label;

              return (
                <Select
                  name={name}
                  onValueChange={value => {
                    // Radix reports an empty value while the current selection is
                    // not part of the options yet (async option lists). It is
                    // never a user choice, and storing it would clear the field
                    // and mark the form dirty on load.
                    if (!value) return;
                    field.onChange(value);
                    onChange?.(value);
                  }}
                  value={field.value || defaultValue || ''}
                  disabled={disabled}
                  onOpenChange={open => {
                    if (open) {
                      handleFocus();
                    } else {
                      handleBlur();
                    }
                  }}
                >
                  <SelectTrigger
                    id={id}
                    name={name}
                    disabled={disabled}
                    data-testid={dataTestId}
                  >
                    <SelectValue placeholder="">{selectedLabel}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {options.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            }}
          </FloatingLabelWrapper>
        </FormControl>
      </FormItem>
    )}
  />
);

export default GenericSelect;
